import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http'
import { downloadStatementPDF } from 'service/GradebookService'
import Notification from 'utils/Notifications'
import {
  PageHeader,
  Skeleton,
  EmptyState,
  ErrorState,
} from 'components'

import StatementsHeader from './components/StatementsHeader/StatementsHeader'
import StatementsList from './components/StatementsList/StatementsList'
import StatementDrawer from './components/StatementDrawer/StatementDrawer'
import styles from './StatementsPage.module.scss'

const StatementsPage = () => {
  const { t } = useTranslation('common')
  const [filters, setFilters] = useState({ semester: '', group: '', subject: '', status: '' })
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [drawer, setDrawer] = useState(null)
  const [rows, setRows] = useState([])
  const [drawerLoading, setDrawerLoading] = useState(false)

  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const STATUS_OPTIONS = [
    { key: '', label: t('statementsPage.statusAll') },
    { key: 'draft', label: t('statementsPage.statusDraft') },
    { key: 'in_progress', label: t('statementsPage.statusInProgress') },
    { key: 'closed', label: t('statementsPage.statusClosed') },
    { key: 'signed', label: t('statementsPage.statusSigned') },
  ]

  const loadList = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const p = {}
      if (filters.semester) p.semester = filters.semester
      if (filters.group) p.group = filters.group
      if (filters.subject) p.subject = filters.subject
      if (filters.status) p.status = filters.status
      const r = await $api.get('/gradebook/statements/', { params: p })
      setList(r.data?.results || r.data || [])
    } catch {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('statementsPage.errorLoad'), type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const openDrawer = async (st) => {
    setDrawer(st)
    setDrawerLoading(true)
    try {
      const r = await $api.get(`/gradebook/statements/${st.id}/`)
      const data = r.data
      const rs = data.rows || data.items || []
      setRows(
        rs.map((row) => ({
          id: row.id,
          student: row.student?.id || row.student,
          studentName: row.student_name || row.student?.full_name || '',
          grade: row.grade ?? '',
          pass: row.pass ?? '',
          absent: row.absent ?? false,
        }))
      )
    } catch {
      setRows([])
    } finally {
      setDrawerLoading(false)
    }
  }

  const closeDrawer = () => {
    setDrawer(null)
    setRows([])
  }

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  const saveRows = async () => {
    if (!drawer) return
    try {
      const items = rows.map((r) => ({
        student: r.student,
        grade: r.grade === '' ? null : r.grade,
        pass: r.pass || null,
        absent: !!r.absent,
      }))
      await $api.post(`/gradebook/statements/${drawer.id}/rows-bulk/`, { items })
      setNotify({ isOpen: true, message: t('statementsPage.rowsSaved'), type: 'success', sound: 'success' })
      loadList()
    } catch {
      setNotify({ isOpen: true, message: t('statementsPage.errorSave'), type: 'error', sound: 'error' })
    }
  }

  const sign = async () => {
    if (!drawer) return
    try {
      await $api.post(`/gradebook/statements/${drawer.id}/sign/`, {})
      setNotify({ isOpen: true, message: t('statementsPage.signed'), type: 'success', sound: 'success' })
      openDrawer(drawer)
      loadList()
    } catch {
      setNotify({ isOpen: true, message: t('statementsPage.errorSign'), type: 'error', sound: 'error' })
    }
  }

  const closeStatement = async () => {
    if (!drawer) return
    try {
      await $api.post(`/gradebook/statements/${drawer.id}/close/`, {})
      setNotify({ isOpen: true, message: t('statementsPage.statementClosed'), type: 'success', sound: 'success' })
      closeDrawer()
      loadList()
    } catch {
      setNotify({ isOpen: true, message: t('statementsPage.errorClose'), type: 'error', sound: 'error' })
    }
  }

  const downloadPdf = async () => {
    if (!drawer) return
    try {
      await downloadStatementPDF(drawer.id)
    } catch {
      setNotify({ isOpen: true, message: t('statementsPage.errorLoad'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('statementsPage.title', 'Ведомости')}
        subtitle={t('statementsPage.subtitle', 'Управление ведомостями оценок')}
      />

      <StatementsHeader
        filters={filters}
        setFilters={setFilters}
        statusOptions={STATUS_OPTIONS}
      />

      {loading ? (
        <Skeleton variant="table" rows={6} cols={5} />
      ) : loadError ? (
        <ErrorState title={t('statementsPage.errorLoad')} onRetry={loadList} />
      ) : list.length === 0 ? (
        <EmptyState iconName="file" title={t('statementsPage.title', 'Ведомости')} description={t('statementsPage.no_data', 'Нет ведомостей по выбранным фильтрам')} />
      ) : (
        <StatementsList list={list} onOpen={openDrawer} />
      )}

      {drawer && (
        <StatementDrawer
          drawer={drawer}
          rows={rows}
          drawerLoading={drawerLoading}
          onClose={closeDrawer}
          updateRow={updateRow}
          onSaveRows={saveRows}
          onSign={sign}
          onDownloadPdf={downloadPdf}
          onCloseStatement={closeStatement}
        />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default StatementsPage
